/**
 * Compliance Hub - Unified Compliance Management Dashboard
 * 
 * Consolidates all P0 critical compliance features:
 * 1. HOPE Data Integration - Full assessment workflow
 * 2. F2F Encounter Workflow - Physician attestation management
 * 3. Medication Reconciliation - Full reconciliation beyond comfort kits
 * 4. Family Teach-back Documentation - Education verification tracking
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { IQIESExportTab } from "./compliance-hub";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ClipboardList, Stethoscope, Pill, GraduationCap, Shield,
  CheckCircle, AlertTriangle, Clock, FileText, Users, Heart,
  RefreshCw, Loader2, Plus, Eye, FileSignature, Calendar,
  ChevronRight, AlertCircle, Activity, Brain, Sparkles,
  ArrowRight, XCircle, Search, UserCheck, MessageCircle,
  PlayCircle, SkipForward, X, Info, ChevronLeft, Download
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tour steps configuration
const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Compliance Hub Tour",
    content: "This guided tour will walk you through completing compliance workflows. You'll learn how to track HOPE assessments, manage F2F encounters, complete medication reconciliations, and verify family teach-backs.",
    target: null,
    action: null
  },
  {
    id: "create-demo",
    title: "Step 1: Create Demo Data",
    content: "First, let's create some demo patients with pending compliance items. Click the 'Create Demo Data' button to generate test data.",
    target: "create-demo-btn",
    action: "click-demo"
  },
  {
    id: "overview",
    title: "Step 2: Review Compliance Score",
    content: "The Overview tab shows your overall compliance score. Notice the priority actions panel highlighting items that need immediate attention.",
    target: "overview-tab",
    action: "navigate-overview"
  },
  {
    id: "medrec-tab",
    title: "Step 3: Medication Reconciliation",
    content: "Navigate to the Med Rec tab to see patients requiring medication reconciliation. This is critical for CMS compliance.",
    target: "medication-tab",
    action: "navigate-medrec"
  },
  {
    id: "medrec-complete",
    title: "Step 4: Complete a Reconciliation",
    content: "Click on a patient to open the reconciliation form. Review their medications, document any discrepancies, and mark as complete.",
    target: null,
    action: "demo-medrec"
  },
  {
    id: "teachback-tab",
    title: "Step 5: Family Teach-back",
    content: "Now let's verify a family teach-back. Navigate to the Teach-back tab to see pending education verifications.",
    target: "teachback-tab",
    action: "navigate-teachback"
  },
  {
    id: "teachback-verify",
    title: "Step 6: Verify Understanding",
    content: "Click on a pending teach-back to open the verification form. Document the family's understanding level and whether re-teaching is needed.",
    target: null,
    action: "demo-teachback"
  },
  {
    id: "complete",
    title: "Tour Complete!",
    content: "You've completed the Compliance Hub tour! You now know how to track compliance status, complete medication reconciliations, and verify family teach-backs. Your compliance score should have improved.",
    target: null,
    action: null
  }
];

// Status colors
const STATUS_COLORS = {
  complete: "bg-green-500/20 text-green-400 border-green-500/30",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  overdue: "bg-red-500/20 text-red-400 border-red-500/30",
  at_risk: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  not_started: "bg-gray-500/20 text-gray-400 border-gray-500/30"
};


export default function ComplianceHub() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [clearingDemo, setClearingDemo] = useState(false);
  
  // Tour states
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourDemoCreated, setTourDemoCreated] = useState(false);
  
  // Data states
  const [hopeStatus, setHopeStatus] = useState(null);
  const [f2fEncounters, setF2fEncounters] = useState(null);
  const [medReconciliation, setMedReconciliation] = useState(null);
  const [teachBack, setTeachBack] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Dialog states
  const [showHopeDialog, setShowHopeDialog] = useState(false);
  const [showF2FDialog, setShowF2FDialog] = useState(false);
  const [showMedRecDialog, setShowMedRecDialog] = useState(false);
  const [showTeachBackDialog, setShowTeachBackDialog] = useState(false);
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  
  const token = localStorage.getItem("token");

  // Create demo compliance data
  const createDemoData = async (scenario = "all") => {
    try {
      setCreatingDemo(true);
      const res = await fetch(`${API_URL}/api/compliance-hub/demo/create-test-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ scenario })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Demo data created! ${data.created.patients.length} patients added.`);
        setShowDemoDialog(false);
        fetchComplianceData();
      } else {
        toast.error("Failed to create demo data");
      }
    } catch (error) {
      toast.error("Error creating demo data");
    } finally {
      setCreatingDemo(false);
    }
  };

  // Clear demo data
  const clearDemoData = async () => {
    try {
      setClearingDemo(true);
      const res = await fetch(`${API_URL}/api/compliance-hub/demo/clear-test-data`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
        toast.success(`Cleared ${total} demo records`);
        fetchComplianceData();
      } else {
        toast.error("Failed to clear demo data");
      }
    } catch (error) {
      toast.error("Error clearing demo data");
    } finally {
      setClearingDemo(false);
    }
  };

  // Tour control functions
  const startTour = () => {
    setTourActive(true);
    setTourStep(0);
    setTourDemoCreated(false);
  };

  const endTour = () => {
    setTourActive(false);
    setTourStep(0);
  };

  const nextTourStep = async () => {
    const currentStep = TOUR_STEPS[tourStep];
    
    // Execute action for current step before moving to next
    if (currentStep.action === "click-demo" && !tourDemoCreated) {
      await createDemoData("all");
      setTourDemoCreated(true);
    } else if (currentStep.action === "navigate-overview") {
      setActiveTab("overview");
    } else if (currentStep.action === "navigate-medrec") {
      setActiveTab("medication");
    } else if (currentStep.action === "navigate-teachback") {
      setActiveTab("teachback");
    }
    
    if (tourStep < TOUR_STEPS.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      endTour();
      toast.success("Tour completed! You're now ready to manage compliance.");
    }
  };

  const prevTourStep = () => {
    if (tourStep > 0) {
      setTourStep(tourStep - 1);
    }
  };

  // Fetch all compliance data
  const fetchComplianceData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch HOPE status via compliance-hub unified endpoint
      const hopeRes = await fetch(`${API_URL}/api/compliance-hub/hope/compliance-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (hopeRes.ok) setHopeStatus(await hopeRes.json());
      
      // Fetch F2F encounters via compliance-hub unified endpoint
      const f2fRes = await fetch(`${API_URL}/api/compliance-hub/f2f/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (f2fRes.ok) setF2fEncounters(await f2fRes.json());
      
      // Fetch medication reconciliation status
      const medRes = await fetch(`${API_URL}/api/compliance-hub/medication-reconciliation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (medRes.ok) setMedReconciliation(await medRes.json());
      
      // Fetch teach-back status
      const teachRes = await fetch(`${API_URL}/api/compliance-hub/teach-back/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (teachRes.ok) setTeachBack(await teachRes.json());
      
    } catch (error) {
      console.error("Compliance data fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData]);

  return (
    <div className={`min-h-screen p-6 ${isDark ? "bg-slate-950 text-white" : "bg-gray-50 text-gray-900"}`} data-testid="compliance-hub">
      {/* Tour Overlay */}
      {tourActive && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 pointer-events-auto" onClick={endTour} />
          
          {/* Tour Card */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
            <Card className={`w-[480px] shadow-2xl ${isDark ? "bg-slate-900 border-slate-700" : "bg-white"}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    Step {tourStep + 1} of {TOUR_STEPS.length}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={endTour} className="h-8 w-8 p-0">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <CardTitle className="text-lg flex items-center gap-2 mt-2">
                  <PlayCircle className="w-5 h-5 text-emerald-400" />
                  {TOUR_STEPS[tourStep].title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {TOUR_STEPS[tourStep].content}
                </p>
                
                {/* Progress bar */}
                <div className="mb-4">
                  <Progress value={((tourStep + 1) / TOUR_STEPS.length) * 100} className="h-2" />
                </div>
                
                {/* Action hint */}
                {TOUR_STEPS[tourStep].action && (
                  <div className={`p-3 rounded-lg mb-4 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <Info className="w-4 h-4 text-blue-400" />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        {TOUR_STEPS[tourStep].action === "click-demo" && "Click 'Next' to create demo patients"}
                        {TOUR_STEPS[tourStep].action === "navigate-overview" && "Click 'Next' to view the Overview"}
                        {TOUR_STEPS[tourStep].action === "navigate-medrec" && "Click 'Next' to go to Med Rec tab"}
                        {TOUR_STEPS[tourStep].action === "demo-medrec" && "In a real workflow, you would click a patient to complete reconciliation"}
                        {TOUR_STEPS[tourStep].action === "navigate-teachback" && "Click 'Next' to go to Teach-back tab"}
                        {TOUR_STEPS[tourStep].action === "demo-teachback" && "In a real workflow, you would click to verify understanding"}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={prevTourStep}
                    disabled={tourStep === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={endTour}>
                      Skip Tour
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={nextTourStep}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {tourStep === TOUR_STEPS.length - 1 ? "Finish" : "Next"}
                      {tourStep < TOUR_STEPS.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Compliance Hub</h1>
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              CMS CoP, HOPE, F2F, & Documentation Compliance
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={startTour} 
            variant="outline" 
            size="sm"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            data-testid="start-tour-btn"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Quick Tour
          </Button>
          <div className="w-px h-6 bg-slate-700" />
          <Button 
            onClick={() => setShowDemoDialog(true)} 
            variant="outline" 
            size="sm"
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
            data-testid="create-demo-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Create Demo Data
          </Button>
          <Button 
            onClick={clearDemoData} 
            variant="outline" 
            size="sm"
            disabled={clearingDemo}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="clear-demo-btn"
          >
            {clearingDemo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
            Clear Demo
          </Button>
          <div className="w-px h-6 bg-slate-700 mx-1" />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search patients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 w-64 ${isDark ? "bg-slate-900 border-slate-700" : ""}`}
            />
          </div>
          <Button onClick={fetchComplianceData} disabled={loading} variant="outline" data-testid="refresh-compliance-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {/* HOPE Card */}
        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}
          onClick={() => setActiveTab("hope")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <ClipboardList className="w-5 h-5 text-blue-400" />
              <Badge className={hopeStatus?.overdue_count > 0 ? STATUS_COLORS.overdue : STATUS_COLORS.complete}>
                {hopeStatus?.overdue_count > 0 ? "Action Needed" : "On Track"}
              </Badge>
            </div>
            <p className="text-2xl font-bold">{hopeStatus?.pending_count || 0}</p>
            <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>HOPE Assessments Due</p>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">{hopeStatus?.complete_count || 0} Complete</span>
              <span className="text-red-400">{hopeStatus?.overdue_count || 0} Overdue</span>
            </div>
          </CardContent>
        </Card>

        {/* F2F Card */}
        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}
          onClick={() => setActiveTab("f2f")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Stethoscope className="w-5 h-5 text-purple-400" />
              <Badge className={f2fEncounters?.critical_count > 0 ? STATUS_COLORS.overdue : STATUS_COLORS.complete}>
                {f2fEncounters?.critical_count > 0 ? "Critical" : "Compliant"}
              </Badge>
            </div>
            <p className="text-2xl font-bold">{f2fEncounters?.pending_count || 0}</p>
            <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>F2F Encounters Pending</p>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-yellow-400">{f2fEncounters?.due_this_week || 0} This Week</span>
              <span className="text-red-400">{f2fEncounters?.overdue_count || 0} Overdue</span>
            </div>
          </CardContent>
        </Card>

        {/* Medication Reconciliation Card */}
        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}
          onClick={() => setActiveTab("medication")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Pill className="w-5 h-5 text-green-400" />
              <Badge className={medReconciliation?.incomplete_count > 0 ? STATUS_COLORS.pending : STATUS_COLORS.complete}>
                {medReconciliation?.incomplete_count > 0 ? "Incomplete" : "Complete"}
              </Badge>
            </div>
            <p className="text-2xl font-bold">{medReconciliation?.incomplete_count || 0}</p>
            <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>Med Rec Incomplete</p>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">{medReconciliation?.complete_count || 0} Complete</span>
              <span className="text-yellow-400">{medReconciliation?.in_progress || 0} In Progress</span>
            </div>
          </CardContent>
        </Card>

        {/* Teach-back Card */}
        <Card 
          className={`cursor-pointer hover:shadow-lg transition-shadow ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}
          onClick={() => setActiveTab("teachback")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <GraduationCap className="w-5 h-5 text-orange-400" />
              <Badge className={teachBack?.unverified_count > 0 ? STATUS_COLORS.pending : STATUS_COLORS.complete}>
                {teachBack?.unverified_count > 0 ? "Verify" : "Verified"}
              </Badge>
            </div>
            <p className="text-2xl font-bold">{teachBack?.unverified_count || 0}</p>
            <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>Teach-back Unverified</p>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">{teachBack?.verified_count || 0} Verified</span>
              <span className="text-yellow-400">{teachBack?.pending_count || 0} Pending</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid grid-cols-6 mb-4 ${isDark ? "bg-slate-900" : "bg-gray-100"}`}>
          <TabsTrigger value="overview" className="text-xs" data-testid="overview-tab">
            <Shield className="w-3 h-3 mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="hope" className="text-xs" data-testid="hope-tab">
            <ClipboardList className="w-3 h-3 mr-1" /> HOPE
          </TabsTrigger>
          <TabsTrigger value="f2f" className="text-xs" data-testid="f2f-tab">
            <Stethoscope className="w-3 h-3 mr-1" /> F2F
          </TabsTrigger>
          <TabsTrigger value="medication" className="text-xs" data-testid="medication-tab">
            <Pill className="w-3 h-3 mr-1" /> Med Rec
          </TabsTrigger>
          <TabsTrigger value="teachback" className="text-xs" data-testid="teachback-tab">
            <GraduationCap className="w-3 h-3 mr-1" /> Teach-back
          </TabsTrigger>
          <TabsTrigger value="iqies" className="text-xs" data-testid="iqies-tab">
            <FileText className="w-3 h-3 mr-1" /> iQIES Export
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <OverviewTab 
            hopeStatus={hopeStatus}
            f2fEncounters={f2fEncounters}
            medReconciliation={medReconciliation}
            teachBack={teachBack}
            isDark={isDark}
            onNavigate={setActiveTab}
          />
        </TabsContent>

        {/* HOPE Tab */}
        <TabsContent value="hope">
          <HOPETab 
            data={hopeStatus}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchComplianceData}
            token={token}
          />
        </TabsContent>

        {/* F2F Tab */}
        <TabsContent value="f2f">
          <F2FTab 
            data={f2fEncounters}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchComplianceData}
            token={token}
          />
        </TabsContent>

        {/* Medication Tab */}
        <TabsContent value="medication">
          <MedicationReconciliationTab 
            data={medReconciliation}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchComplianceData}
            token={token}
          />
        </TabsContent>

        {/* Teach-back Tab */}
        <TabsContent value="teachback">
          <TeachBackTab 
            data={teachBack}
            loading={loading}
            isDark={isDark}
            onRefresh={fetchComplianceData}
            token={token}
          />
        </TabsContent>

        {/* iQIES Export Tab */}
        <TabsContent value="iqies">
          <IQIESExportTab 
            loading={loading}
            isDark={isDark}
            onRefresh={fetchComplianceData}
            token={token}
          />
        </TabsContent>
      </Tabs>

      {/* Demo Data Creation Dialog */}
      <Dialog open={showDemoDialog} onOpenChange={setShowDemoDialog}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Create Demo Compliance Data
            </DialogTitle>
            <DialogDescription>
              Generate test patients with pending compliance items to demo workflows.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              This will create 3 demo patients with realistic compliance items:
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className={`cursor-pointer hover:border-emerald-500/50 transition-colors ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                onClick={() => createDemoData("all")}
              >
                <CardContent className="p-4 text-center">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  <p className="font-medium">All Categories</p>
                  <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    HOPE, F2F, Med Rec, Teach-back
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer hover:border-blue-500/50 transition-colors ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                onClick={() => createDemoData("hope")}
              >
                <CardContent className="p-4 text-center">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                  <p className="font-medium">HOPE Only</p>
                  <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    Pending assessments
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer hover:border-purple-500/50 transition-colors ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                onClick={() => createDemoData("f2f")}
              >
                <CardContent className="p-4 text-center">
                  <Stethoscope className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                  <p className="font-medium">F2F Only</p>
                  <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    Pending encounters
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer hover:border-amber-500/50 transition-colors ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                onClick={() => createDemoData("medrec")}
              >
                <CardContent className="p-4 text-center">
                  <Pill className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                  <p className="font-medium">Med Rec Only</p>
                  <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    Pending reconciliations
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer hover:border-cyan-500/50 transition-colors col-span-2 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}
                onClick={() => createDemoData("teachback")}
              >
                <CardContent className="p-4 text-center">
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-cyan-400" />
                  <p className="font-medium">Teach-back Only</p>
                  <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    Pending education verifications
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {creatingDemo && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-400 mr-2" />
                <span className={isDark ? "text-gray-400" : "text-gray-600"}>Creating demo data...</span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDemoDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function OverviewTab({ hopeStatus, f2fEncounters, medReconciliation, teachBack, isDark, onNavigate }) {
  // Calculate overall compliance score
  const totalItems = 
    (hopeStatus?.total_count || 0) + 
    (f2fEncounters?.total_count || 0) + 
    (medReconciliation?.total_count || 0) + 
    (teachBack?.total_count || 0);
  
  const compliantItems = 
    (hopeStatus?.complete_count || 0) + 
    (f2fEncounters?.complete_count || 0) + 
    (medReconciliation?.complete_count || 0) + 
    (teachBack?.verified_count || 0);
  
  const complianceScore = totalItems > 0 ? Math.round((compliantItems / totalItems) * 100) : 100;

  return (
    <div className="space-y-6">
      {/* Compliance Score */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400" />
            Overall Compliance Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48" cy="48" r="40"
                  fill="none"
                  stroke={isDark ? "#334155" : "#e5e7eb"}
                  strokeWidth="8"
                />
                <circle
                  cx="48" cy="48" r="40"
                  fill="none"
                  stroke={complianceScore >= 90 ? "#22c55e" : complianceScore >= 70 ? "#eab308" : "#ef4444"}
                  strokeWidth="8"
                  strokeDasharray={`${complianceScore * 2.51} 251`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-2xl font-bold ${
                  complianceScore >= 90 ? "text-green-400" :
                  complianceScore >= 70 ? "text-yellow-400" : "text-red-400"
                }`}>
                  {complianceScore}%
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">HOPE</p>
                  <p className="font-medium">{hopeStatus?.complete_count || 0}/{hopeStatus?.total_count || 0}</p>
                </div>
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">F2F</p>
                  <p className="font-medium">{f2fEncounters?.complete_count || 0}/{f2fEncounters?.total_count || 0}</p>
                </div>
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">Med Rec</p>
                  <p className="font-medium">{medReconciliation?.complete_count || 0}/{medReconciliation?.total_count || 0}</p>
                </div>
                <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">Teach-back</p>
                  <p className="font-medium">{teachBack?.verified_count || 0}/{teachBack?.total_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Priority Actions */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            Priority Actions Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(hopeStatus?.overdue_count > 0 || f2fEncounters?.critical_count > 0 || 
              medReconciliation?.incomplete_count > 0 || teachBack?.unverified_count > 0) ? (
              <>
                {hopeStatus?.overdue_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-red-900/20 border border-red-500/30" : "bg-red-50"}`}
                    onClick={() => onNavigate("hope")}
                  >
                    <div className="flex items-center gap-3">
                      <ClipboardList className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium text-red-400">{hopeStatus.overdue_count} Overdue HOPE Assessments</p>
                        <p className="text-xs text-gray-500">CMS requires timely HOPE completion</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-400" />
                  </div>
                )}
                
                {f2fEncounters?.critical_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-red-900/20 border border-red-500/30" : "bg-red-50"}`}
                    onClick={() => onNavigate("f2f")}
                  >
                    <div className="flex items-center gap-3">
                      <Stethoscope className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium text-red-400">{f2fEncounters.critical_count} Critical F2F Encounters</p>
                        <p className="text-xs text-gray-500">Required for recertification compliance</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-400" />
                  </div>
                )}
                
                {medReconciliation?.incomplete_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-yellow-900/20 border border-yellow-500/30" : "bg-yellow-50"}`}
                    onClick={() => onNavigate("medication")}
                  >
                    <div className="flex items-center gap-3">
                      <Pill className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="font-medium text-yellow-400">{medReconciliation.incomplete_count} Med Reconciliations Incomplete</p>
                        <p className="text-xs text-gray-500">Required for survey compliance</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-yellow-400" />
                  </div>
                )}
                
                {teachBack?.unverified_count > 0 && (
                  <div 
                    className={`p-3 rounded flex items-center justify-between cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-yellow-900/20 border border-yellow-500/30" : "bg-yellow-50"}`}
                    onClick={() => onNavigate("teachback")}
                  >
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="font-medium text-yellow-400">{teachBack.unverified_count} Teach-backs Unverified</p>
                        <p className="text-xs text-gray-500">Family education acknowledgment needed</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-yellow-400" />
                  </div>
                )}
              </>
            ) : (
              <div className={`p-6 text-center rounded ${isDark ? "bg-green-900/20 border border-green-500/30" : "bg-green-50"}`}>
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="font-medium text-green-400">All Clear!</p>
                <p className="text-sm text-gray-500">No priority compliance actions required</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CMS Requirements Checklist */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-400" />
            CMS CoP Requirements Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {[
              { name: "HOPE Admission Assessment", status: hopeStatus?.admission_complete ? "complete" : "pending" },
              { name: "HOPE Updates (15 Days)", status: hopeStatus?.updates_current ? "complete" : "pending" },
              { name: "F2F Encounters", status: f2fEncounters?.all_compliant ? "complete" : "pending" },
              { name: "Physician Attestations", status: f2fEncounters?.attestations_complete ? "complete" : "pending" },
              { name: "Medication Reconciliation", status: medReconciliation?.all_complete ? "complete" : "pending" },
              { name: "Family Education Verified", status: teachBack?.all_verified ? "complete" : "pending" },
            ].map((item, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-2 rounded ${
                  item.status === "complete" 
                    ? isDark ? "bg-green-900/20 border border-green-500/30" : "bg-green-50"
                    : isDark ? "bg-yellow-900/20 border border-yellow-500/30" : "bg-yellow-50"
                }`}
              >
                <span className="text-sm">{item.name}</span>
                {item.status === "complete" ? (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                ) : (
                  <Clock className="w-4 h-4 text-yellow-400" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function HOPETab({ data, loading, isDark, onRefresh, token }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/hope/patients-pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setPatients(result.patients || []);
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchPatients();
  }, [token]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">HOPE Assessment Compliance</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* HOPE Types */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { type: "Admission", count: data?.by_type?.admission || 0, due: data?.by_type?.admission_due || 0 },
          { type: "Update 1", count: data?.by_type?.update_1 || 0, due: data?.by_type?.update_1_due || 0 },
          { type: "Update 2", count: data?.by_type?.update_2 || 0, due: data?.by_type?.update_2_due || 0 },
          { type: "SFV", count: data?.by_type?.sfv || 0, due: data?.by_type?.sfv_due || 0 },
          { type: "Discharge", count: data?.by_type?.discharge || 0, due: data?.by_type?.discharge_due || 0 },
        ].map((item, i) => (
          <Card key={i} className={isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50"}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-gray-500">{item.type}</p>
              <p className="text-xl font-bold">{item.count}</p>
              {item.due > 0 && (
                <Badge className="mt-1 bg-yellow-500/20 text-yellow-400 text-xs">
                  {item.due} due
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending HOPE List */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Pending HOPE Assessments</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {patients.length > 0 ? patients.map((patient, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-3 mb-2 rounded cursor-pointer hover:bg-opacity-80 ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
              >
                <div>
                  <p className="font-medium">{patient.patient_name}</p>
                  <p className="text-xs text-gray-500">
                    {patient.hope_type} | Due: {new Date(patient.due_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[patient.status]}>
                    {patient.status}
                  </Badge>
                  <Button size="sm" variant="outline">
                    <FileText className="w-4 h-4 mr-1" /> Open
                  </Button>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="font-medium">All HOPE Assessments Complete</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* iQIES Export Status */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-cyan-400" />
            iQIES Submission Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">Next batch: {data?.next_batch_date || "Not scheduled"}</p>
              <p className="text-xs text-gray-500">{data?.pending_export || 0} records pending export</p>
            </div>
            <Button variant="outline" size="sm">
              <Calendar className="w-4 h-4 mr-1" /> Schedule Export
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function F2FTab({ data, loading, isDark, onRefresh, token }) {
  const [encounters, setEncounters] = useState([]);
  const [showAttestationDialog, setShowAttestationDialog] = useState(false);
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  
  useEffect(() => {
    const fetchEncounters = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/f2f/all-pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setEncounters(result.encounters || []);
        }
      } catch (error) {
        console.error("Error fetching F2F encounters:", error);
      }
    };
    fetchEncounters();
  }, [token]);

  const scheduleF2F = async (encounterId, date) => {
    try {
      const res = await fetch(`${API_URL}/api/f2f-scheduler/schedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ encounter_id: encounterId, scheduled_date: date })
      });
      if (res.ok) {
        toast.success("F2F encounter scheduled");
        onRefresh();
      }
    } catch (error) {
      toast.error("Failed to schedule F2F");
    }
  };

  const completeAttestation = async (encounterId, attestationData) => {
    try {
      const res = await fetch(`${API_URL}/api/f2f-scheduler/complete-attestation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ encounter_id: encounterId, ...attestationData })
      });
      if (res.ok) {
        toast.success("Attestation completed");
        setShowAttestationDialog(false);
        onRefresh();
      }
    } catch (error) {
      toast.error("Failed to complete attestation");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">F2F Encounter Management</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* F2F Requirements Info */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Medicare F2F Requirements (42 CFR 418.22)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
              <p className="font-medium">Initial Certification</p>
              <p className="text-xs text-gray-500">Within 30 days before/after admission</p>
            </div>
            <div className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
              <p className="font-medium">Recertification</p>
              <p className="text-xs text-gray-500">Within 30 days before new benefit period</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Encounters */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Pending F2F Encounters</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-72">
            {encounters.length > 0 ? encounters.map((encounter, i) => (
              <div 
                key={i} 
                className={`p-3 mb-2 rounded border-l-4 ${
                  encounter.compliance_status === "critical" ? "border-l-red-500" :
                  encounter.compliance_status === "at_risk" ? "border-l-yellow-500" : "border-l-green-500"
                } ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{encounter.patient_name}</p>
                    <p className="text-xs text-gray-500">
                      {encounter.requirement_type} | Benefit Period {encounter.benefit_period}
                    </p>
                    <p className="text-xs text-gray-500">
                      Deadline: {new Date(encounter.f2f_deadline).toLocaleDateString()}
                    </p>
                    {encounter.assigned_performer_name && (
                      <p className="text-xs text-cyan-400 mt-1">
                        Assigned: {encounter.assigned_performer_name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Badge className={STATUS_COLORS[encounter.status]}>
                      {encounter.status}
                    </Badge>
                    {encounter.status === "pending" && (
                      <Button size="sm" variant="outline" className="text-xs">
                        <Calendar className="w-3 h-3 mr-1" /> Schedule
                      </Button>
                    )}
                    {encounter.status === "scheduled" && !encounter.attestation_completed && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-xs"
                        onClick={() => {
                          setSelectedEncounter(encounter);
                          setShowAttestationDialog(true);
                        }}
                      >
                        <FileSignature className="w-3 h-3 mr-1" /> Attest
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="font-medium">All F2F Encounters Complete</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Attestation Dialog */}
      <Dialog open={showAttestationDialog} onOpenChange={setShowAttestationDialog}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <DialogHeader>
            <DialogTitle>Complete F2F Attestation</DialogTitle>
            <DialogDescription>
              Document the face-to-face encounter and clinical findings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Encounter Date</Label>
              <Input type="date" className={isDark ? "bg-slate-800 border-slate-700" : ""} />
            </div>
            <div className="space-y-2">
              <Label>Clinical Findings Supporting Terminal Prognosis</Label>
              <Textarea 
                placeholder="Document clinical findings that support a prognosis of 6 months or less..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
                rows={4}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="attestation-confirm" />
              <label htmlFor="attestation-confirm" className="text-sm">
                I attest that the above clinical findings were gathered during the F2F encounter and support a terminal prognosis
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttestationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => completeAttestation(selectedEncounter?.id, {})}>
              Complete Attestation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MedicationReconciliationTab({ data, loading, isDark, onRefresh, token }) {
  const [patients, setPatients] = useState([]);
  const [showMedRecDialog, setShowMedRecDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/medication-reconciliation/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setPatients(result.patients || []);
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchPatients();
  }, [token]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Medication Reconciliation</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Reconciliation Status */}
      <div className="grid grid-cols-4 gap-3">
        <Card className={`${isDark ? "bg-green-900/20 border-green-500/30" : "bg-green-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-400">{data?.complete_count || 0}</p>
            <p className="text-xs text-gray-500">Complete</p>
          </CardContent>
        </Card>
        <Card className={`${isDark ? "bg-yellow-900/20 border-yellow-500/30" : "bg-yellow-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{data?.in_progress || 0}</p>
            <p className="text-xs text-gray-500">In Progress</p>
          </CardContent>
        </Card>
        <Card className={`${isDark ? "bg-red-900/20 border-red-500/30" : "bg-red-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-400">{data?.incomplete_count || 0}</p>
            <p className="text-xs text-gray-500">Incomplete</p>
          </CardContent>
        </Card>
        <Card className={`${isDark ? "bg-blue-900/20 border-blue-500/30" : "bg-blue-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-400">{data?.discharge_pending || 0}</p>
            <p className="text-xs text-gray-500">Discharge Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Reconciliation Requirements */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Required Reconciliation Events</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {[
              { event: "Admission", desc: "All home medications" },
              { event: "Change in Condition", desc: "New or changed meds" },
              { event: "Discharge", desc: "Final disposition + destruction" }
            ].map((item, i) => (
              <div key={i} className={`p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
                <p className="font-medium">{item.event}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Patient List */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Patients Requiring Reconciliation</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {patients.length > 0 ? patients.map((patient, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-3 mb-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
              >
                <div>
                  <p className="font-medium">{patient.patient_name}</p>
                  <p className="text-xs text-gray-500">
                    {patient.medication_count} medications | Last reconciled: {patient.last_reconciled || "Never"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[patient.status]}>
                    {patient.status}
                  </Badge>
                  <Button size="sm" variant="outline">
                    <Pill className="w-4 h-4 mr-1" /> Reconcile
                  </Button>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="font-medium">All Medications Reconciled</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

function TeachBackTab({ data, loading, isDark, onRefresh, token }) {
  const [teachings, setTeachings] = useState([]);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [selectedTeaching, setSelectedTeaching] = useState(null);
  
  useEffect(() => {
    const fetchTeachings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/compliance-hub/teach-back/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setTeachings(result.teachings || []);
        }
      } catch (error) {
        console.error("Error fetching teach-backs:", error);
      }
    };
    fetchTeachings();
  }, [token]);

  const verifyTeachBack = async (teachingId, verificationData) => {
    try {
      const res = await fetch(`${API_URL}/api/compliance-hub/teach-back/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ teaching_id: teachingId, ...verificationData })
      });
      if (res.ok) {
        toast.success("Teach-back verified");
        setShowVerifyDialog(false);
        onRefresh();
      }
    } catch (error) {
      toast.error("Failed to verify teach-back");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Family Teach-back Documentation</h3>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={`${isDark ? "bg-green-900/20 border-green-500/30" : "bg-green-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-400">{data?.verified_count || 0}</p>
            <p className="text-xs text-gray-500">Verified</p>
          </CardContent>
        </Card>
        <Card className={`${isDark ? "bg-yellow-900/20 border-yellow-500/30" : "bg-yellow-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{data?.pending_count || 0}</p>
            <p className="text-xs text-gray-500">Pending Verification</p>
          </CardContent>
        </Card>
        <Card className={`${isDark ? "bg-red-900/20 border-red-500/30" : "bg-red-50"}`}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-red-400">{data?.unverified_count || 0}</p>
            <p className="text-xs text-gray-500">Unverified</p>
          </CardContent>
        </Card>
      </div>

      {/* Teach-back Topics */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Required Teaching Topics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              "Medication Administration",
              "Symptom Management",
              "When to Call Hospice",
              "Comfort Kit Use",
              "End of Life Signs",
              "Infection Prevention"
            ].map((topic, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {topic}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Teach-backs */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-sm">Teach-backs Pending Verification</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {teachings.length > 0 ? teachings.map((teaching, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-3 mb-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
              >
                <div>
                  <p className="font-medium">{teaching.patient_name}</p>
                  <p className="text-xs text-gray-500">
                    Topic: {teaching.topic} | Audience: {teaching.audience}
                  </p>
                  <p className="text-xs text-gray-500">
                    Taught: {new Date(teaching.taught_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[teaching.verification_status]}>
                    {teaching.verification_status}
                  </Badge>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setSelectedTeaching(teaching);
                      setShowVerifyDialog(true);
                    }}
                  >
                    <UserCheck className="w-4 h-4 mr-1" /> Verify
                  </Button>
                </div>
              </div>
            )) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p className="font-medium">All Teach-backs Verified</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Verify Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <DialogHeader>
            <DialogTitle>Verify Teach-back Understanding</DialogTitle>
            <DialogDescription>
              Document family/caregiver understanding of teaching provided
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Understanding Level</Label>
              <Select>
                <SelectTrigger className={isDark ? "bg-slate-800 border-slate-700" : ""}>
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Understanding</SelectItem>
                  <SelectItem value="partial">Partial - Needs Reinforcement</SelectItem>
                  <SelectItem value="limited">Limited - Requires Re-teaching</SelectItem>
                  <SelectItem value="unable">Unable to Verify</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Teach-back Questions Asked</Label>
              <Textarea 
                placeholder="Document the questions asked and responses..."
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Person Verified</Label>
              <Input 
                placeholder="Name and relationship to patient"
                className={isDark ? "bg-slate-800 border-slate-700" : ""}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="reteach-needed" />
              <label htmlFor="reteach-needed" className="text-sm">
                Additional teaching needed
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => verifyTeachBack(selectedTeaching?.id, {})}>
              Verify Understanding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
